import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { safeParse } from '../utils';
import { helpTopics } from '../data/helpTopics';
import { STORAGE_TABS, STORAGE_ACTIVE } from '../constants';

type EditorStateContextValue = {
  // Code
  code: string;
  setCode: React.Dispatch<React.SetStateAction<string>>;
  // Active file
  activeFile: string | null;
  setActiveFile: React.Dispatch<React.SetStateAction<string | null>>;
  // Open tabs
  openTabs: string[];
  setOpenTabs: React.Dispatch<React.SetStateAction<string[]>>;
  // Help
  activeHelpId: string;
  setActiveHelpId: React.Dispatch<React.SetStateAction<string>>;
  // Commit
  commitMessage: string;
  setCommitMessage: React.Dispatch<React.SetStateAction<string>>;
  // Selected commit
  selectedCommitId: string;
  setSelectedCommitId: React.Dispatch<React.SetStateAction<string>>;
};

const EditorStateContext = createContext<EditorStateContextValue | null>(null);

type EditorStateProviderProps = {
  children: React.ReactNode;
  initialCommitId?: string;
};

export function EditorStateProvider({ children, initialCommitId = '' }: EditorStateProviderProps) {
  const [code, setCode] = useState('');
  const [activeFile, setActiveFile] = useState<string | null>(
    () => localStorage.getItem(STORAGE_ACTIVE)
  );
  const [openTabs, setOpenTabs] = useState<string[]>(
    () => safeParse<string[]>(localStorage.getItem(STORAGE_TABS)) || []
  );
  const [activeHelpId, setActiveHelpId] = useState(helpTopics[0]?.id || '');
  const [commitMessage, setCommitMessage] = useState('');
  const [selectedCommitId, setSelectedCommitId] = useState(initialCommitId);

  // Persist activeFile to localStorage
  useEffect(() => {
    if (activeFile) {
      localStorage.setItem(STORAGE_ACTIVE, activeFile);
    }
  }, [activeFile]);

  // Persist openTabs to localStorage
  useEffect(() => {
    localStorage.setItem(STORAGE_TABS, JSON.stringify(openTabs));
  }, [openTabs]);

  const value = useMemo<EditorStateContextValue>(
    () => ({
      code,
      setCode,
      activeFile,
      setActiveFile,
      openTabs,
      setOpenTabs,
      activeHelpId,
      setActiveHelpId,
      commitMessage,
      setCommitMessage,
      selectedCommitId,
      setSelectedCommitId,
    }),
    [
      code,
      activeFile,
      openTabs,
      activeHelpId,
      commitMessage,
      selectedCommitId,
    ]
  );

  return <EditorStateContext.Provider value={value}>{children}</EditorStateContext.Provider>;
}

export function useEditorState() {
  const context = useContext(EditorStateContext);
  if (!context) {
    throw new Error('useEditorState must be used within an EditorStateProvider');
  }
  return context;
}
